const authenticationInstitutional = `

### Session based authentication

Similarly to individual clients, institutional clients can also use session based authentication. For instructions on how to set up session-based authentication using the Client Portal API Gateway please refer to the Quickstart guide for individual clients.

A downside to this approach is that it requires a GUI to be present on the machine where the client is running to facilitate authentication. Nonetheless, this approach is a good starting point for testing and prototyping.

---

### OAuth based authentication

In addition to session based authentication via the API Gateway, Interactive Brokers also supports headless Client Portal API authentication via the modified [OAuth 1.0a](https://oauth.net/core/1.0a/) protocol. 
This allows you to authenticate your application with the IBKR backend without having to run the gateway and provides more flexible deployment opportunities.

Authenticating Client Portal API with OAuth is currently only available to institutional clients. It has been successfully used to develop web and mobile apps, 
as well as to integrate with third-party solutions.

---

### Getting access to OAuth at Interactive Brokers

To use OAuth authentication, you will need to register your application with Interactive Brokers. Currently only institutional clients can register their OAuth applications. Retail clients with an individual account type are not eligible to use OAuth.

Depending on the type of application developed, clients will need to either send a request ticket via IBKR Client Portal Support Center, if the API client is developed in-house, or by 
emailing the Web API Onboarding team if the client application is being developed by a third-party vendor with the intention of providing software for other IBKR clients.

#### For apps developed by institutional clients and financial advisors

In order to apply for OAuth access for applications developed in-house, or by a legal affiliate to the account, clients will need to send a request ticket via IBKR Client Portal Support Center. To speed up the process,
please include the following information in your request:

- A confirmation that the application is being developed in-house.
- A list of accounts that will be used with the application.
- The main purpose of the application.

Once the request has been received our development team will process it accordingly. We may ask for additional details at this stage. Once approved, a confirmation message outlining next steps, including access to our Github repository, where the API demo and implementation instructions can be found, as well as access to the [self-service OAuth portal](https://ndcdyn.interactivebrokers.com/sso/Login?action=OAUTH) will be sent out.

#### For apps developed by a third-party vendors

Apps developed by third-party vendors wanting to build software for IBKR clients over OAuth will need to fill in an onboarding questionnaire. The questionnaire can be found [here](https://interactivebrokers.com/images/2015/WebAPIOnboarding.pdf). Once filled in, the questionnaire needs to be sent to the Web API onboarding team at webapionboarding@interactivebrokers.com, where it will be processed accordingly.

---

### Next steps

**Note:** Please note that only **public** keys should be shared with IBKR. Private keys should be kept in a safe place and not shared with anyone, including IBKR staff.

As part of the integration process several pieces of information need to be supplied to IBKR, these include:

- A consumer key. This is a 9 character string or vendor code.
- A pair of private and public encryption keys.

    \`\`\`sh
    openssl rsa -in private_encryption.pem -ouform PEM -pubout -out public_encryption.pem
    \`\`\`

- A pair of private and public signature key.

    \`\`\`sh
    openssl genrsa -out private_esignature.pem 2048
    openssl rsa -in private_signature.pem -ouform PEM -pubout -out public_signature.pem
    \`\`\`

- [Diffie-Hellman](https://en.wikipedia.org/wiki/Diffie%E2%80%93Hellman_key_exchange) parameter file registered with IBKR.

    \`\`\`sh
    openssl dhparam -outform PEM 2048 -out dhparam.pem
    openssl asn1parse < dhparam.pem
    \`\`\`

    
If you are developing the party in-house this information will be uploaded to the self-service portal. For apps developed by third-party vendors information will need to be sent over email.

---
`

export default authenticationInstitutional;